import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Label } from "@radix-ui/react-label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/modal";
import { SuccessIcon } from "../settings-account/assets";

interface SubNameFormProps {
  subName: string;
  onChange: (subName: string) => Promise<boolean>;
}

/** 智能体订阅侧展示名（subName） */
export function SubNameForm({
  subName: initialSubName = "",
  onChange,
}: SubNameFormProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [subName, setSubName] = useState(initialSubName);

  useEffect(() => {
    setSubName(initialSubName);
  }, [initialSubName]);

  const handleCancel = () => {
    setSubName(initialSubName);
  };

  const handleChange = async (next: string) => {
    setIsLoading(true);
    const result = await onChange(next);
    setIsLoading(false);
    if (result) {
      setIsSuccess(true);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setIsSuccess(false);
    }
  };

  const hasChanges = subName !== initialSubName;
  return (
    <div className="flex flex-col gap-4">
      <div className="w-full space-y-2">
        <Label className="text-sm font-medium text-i-normal">
          {t("agent_creation.agent_modal.name")}
        </Label>
        <Input
          value={subName}
          onChange={(e) => setSubName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              !hasChanges ||
                isLoading ||
                isSuccess ||
                handleChange(subName);
            }
          }}
          className="px-3.5 py-2.5 text-i-normal placeholder:text-i-sub rounded-sm focus-visible:ring-0 focus-visible:ring-offset-0 border-[rgba(208,213,221,1)] hover:border-[rgba(102,112,133,1)] focus:border-[rgba(16,24,40,1)] shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
          placeholder={t("agent_creation.agent_modal.name_placeholder")}
        />
      </div>
      <div className="flex justify-end gap-3">
        <Button
          disabled={!isSuccess && !hasChanges}
          variant="outline"
          onClick={() =>
            !hasChanges || isLoading || isSuccess || handleCancel()
          }
          className="px-4 py-3 w-20 text-sm font-medium text-i-default border border-[rgba(208,213,221,1)] hover:border-[rgba(102,112,133,1)] bg-i-white hover:bg-i-white shadow-[0px_1px_2px_rgba(16,24,40,0.05)] rounded-sm"
        >
          {t("settings_account.name_form.cancel")}
        </Button>
        <Button
          disabled={!isSuccess && !hasChanges}
          onClick={() =>
            !hasChanges || isLoading || isSuccess || handleChange(subName)
          }
          className="px-4 py-3 w-20 text-sm font-medium text-i-white rounded-sm bg-[rgba(16,24,40,1)] hover:bg-[rgba(16,24,40,0.9)] shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
        >
          {isLoading ? (
            <Spinner />
          ) : isSuccess ? (
            <SuccessIcon className="w-5 h-5" />
          ) : (
            t("settings_account.name_form.save")
          )}
        </Button>
      </div>
    </div>
  );
}
